import { useState, useCallback, useRef } from 'react';

/**
 * useEntitlementPolling
 * 
 * A hook for polling the /api/v1/app-context endpoint to detect entitlement changes.
 * Uses exponential backoff to minimize server load while ensuring rapid UI updates
 * after a successful payment webhook processed by the backend.
 */
export const useEntitlementPolling = (currentVersion, onUpdate) => {
    const [isPolling, setIsPolling] = useState(false);
    const pollCountRef = useRef(0);
    const timeoutRef = useRef(null);

    const poll = useCallback(async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const token = localStorage.getItem('fb_token'); // Standard token location for this app

            const response = await fetch(`${apiUrl}/api/v1/app-context`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Polling failed');

            const data = await response.json();
            const newVersion = data.profile?.entitlement_version;

            if (newVersion && newVersion !== currentVersion) {
                // Version changed! Webhook was processed.
                setIsPolling(false);
                if (onUpdate) onUpdate(data);
                return;
            }

            // Version hasn't changed, continue polling with backoff
            pollCountRef.current += 1;
            if (pollCountRef.current > 10) {
                // Timeout after ~30-60 seconds of polling
                setIsPolling(false);
                return;
            }

            const backoff = Math.min(1000 * Math.pow(1.5, pollCountRef.current), 10000);
            timeoutRef.current = setTimeout(poll, backoff);

        } catch (error) {
            console.error('Entitlement polling error:', error);
            setIsPolling(false);
        }
    }, [currentVersion, onUpdate]);

    const startPolling = useCallback(() => {
        if (isPolling) return;
        setIsPolling(true);
        pollCountRef.current = 0;
        poll();
    }, [isPolling, poll]);

    const stopPolling = useCallback(() => {
        setIsPolling(false);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, []);

    return { isPolling, startPolling, stopPolling };
};
